/**
 * IP Enforcement Service
 * Simple client-side IP blocking mechanism
 * Note: This is NOT foolproof - determined attackers can bypass it
 * For production, use server-side blocking
 */

import IPBlockingService from './ipBlockingService';

class IPEnforcementService {
    private static currentUserIP: string | null = null;
    private static checkInterval: NodeJS.Timeout | null = null;
    private static isBlocked: boolean = false;

    /**
     * Initialize IP enforcement
     */
    static async initialize() {
        // Get user's IP address
        await this.detectUserIP();
        
        // Start checking if IP is blocked
        this.startMonitoring();
        
        console.log('🛡️ IP Enforcement Service initialized');
    }

    /**
     * Detect user's IP address using public API
     */
    private static async detectUserIP(): Promise<void> {
        try {
            // Try multiple IP detection services for reliability
            const services = [
                'https://api.ipify.org?format=json',
                'https://api.my-ip.io/ip.json',
                'https://ipapi.co/json/'
            ];

            for (const service of services) {
                try {
                    const response = await fetch(service);
                    const data = await response.json();
                    
                    // Different services return IP in different formats
                    this.currentUserIP = data.ip || data.IP || data.query;
                    
                    if (this.currentUserIP) {
                        console.log('🌐 Detected IP:', this.currentUserIP);
                        break;
                    }
                } catch (err) {
                    console.warn(`Failed to get IP from ${service}:`, err);
                    continue;
                }
            }

            if (!this.currentUserIP) {
                console.warn('⚠️ Could not detect IP address');
            }
        } catch (error) {
            console.error('❌ Error detecting IP:', error);
        }
    }

    /**
     * Start monitoring if current IP is blocked
     */
    private static startMonitoring() {
        // Check immediately
        this.checkIfBlocked();
        
        // Then check every 5 seconds
        this.checkInterval = setInterval(() => {
            this.checkIfBlocked();
        }, 5000);
    }

    /**
     * Check if current IP is blocked
     */
    private static async checkIfBlocked() {
        if (!this.currentUserIP) return;

        try {
            const isBlocked = await IPBlockingService.isIPBlocked(this.currentUserIP);
            
            if (isBlocked && !this.isBlocked) {
                // IP just got blocked
                this.isBlocked = true;
                this.blockAccess();
            } else if (!isBlocked && this.isBlocked) {
                // IP was unblocked
                this.isBlocked = false;
                this.restoreAccess();
            }
        } catch (error) {
            console.error('Error checking IP block status:', error);
        }
    }

    /**
     * Block access to the application
     */
    private static blockAccess() {
        console.warn('🚫 Your IP has been blocked!');
        
        // Create blocking overlay
        const overlay = document.createElement('div');
        overlay.id = 'ip-block-overlay';
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            z-index: 999999;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
        `;

        overlay.innerHTML = `
            <div style="
                background: white;
                padding: 40px;
                border-radius: 20px;
                box-shadow: 0 20px 60px rgba(0,0,0,0.3);
                text-align: center;
                max-width: 500px;
                animation: slideIn 0.5s ease-out;
            ">
                <div style="
                    width: 80px;
                    height: 80px;
                    margin: 0 auto 20px;
                    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 40px;
                ">
                    🚫
                </div>
                <h1 style="
                    margin: 0 0 10px;
                    color: #2d3748;
                    font-size: 28px;
                    font-weight: 700;
                ">Access Blocked</h1>
                <p style="
                    margin: 0 0 20px;
                    color: #718096;
                    font-size: 16px;
                    line-height: 1.6;
                ">
                    Your IP address <strong style="color: #e53e3e; font-family: monospace;">${this.currentUserIP}</strong> 
                    has been blocked due to suspicious activity.
                </p>
                <p style="
                    margin: 0;
                    color: #a0aec0;
                    font-size: 14px;
                ">
                    If you believe this is an error, please contact the administrator.
                </p>
                <div style="
                    margin-top: 30px;
                    padding: 15px;
                    background: #fff5f5;
                    border-left: 4px solid #e53e3e;
                    border-radius: 4px;
                    text-align: left;
                ">
                    <p style="
                        margin: 0;
                        color: #742a2a;
                        font-size: 13px;
                        font-weight: 600;
                    ">
                        🔒 Security Notice
                    </p>
                    <p style="
                        margin: 5px 0 0;
                        color: #742a2a;
                        font-size: 12px;
                        line-height: 1.5;
                    ">
                        This page has been restricted to protect our systems. 
                        All access attempts are logged.
                    </p>
                </div>
            </div>
            <style>
                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateY(-50px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            </style>
        `;

        document.body.appendChild(overlay);

        // Disable scrolling
        document.body.style.overflow = 'hidden';

        // Disable all interactions
        document.addEventListener('click', this.preventInteraction, true);
        document.addEventListener('keydown', this.preventInteraction, true);
    }

    /**
     * Restore access (if IP was unblocked)
     */
    private static restoreAccess() {
        console.log('✅ Your IP has been unblocked!');
        
        const overlay = document.getElementById('ip-block-overlay');
        if (overlay) {
            overlay.remove();
        }

        // Re-enable scrolling
        document.body.style.overflow = '';

        // Re-enable interactions
        document.removeEventListener('click', this.preventInteraction, true);
        document.removeEventListener('keydown', this.preventInteraction, true);

        // Reload page to restore full functionality
        setTimeout(() => {
            window.location.reload();
        }, 1000);
    }

    /**
     * Prevent all user interactions
     */
    private static preventInteraction(e: Event) {
        e.stopPropagation();
        e.preventDefault();
        return false;
    }

    /**
     * Get current user's IP
     */
    static getCurrentIP(): string | null {
        return this.currentUserIP;
    }

    /**
     * Check if current user is blocked
     */
    static isCurrentUserBlocked(): boolean {
        return this.isBlocked;
    }

    /**
     * Stop monitoring
     */
    static stop() {
        if (this.checkInterval) {
            clearInterval(this.checkInterval);
            this.checkInterval = null;
        }
    }
}

export default IPEnforcementService;
